import i18n from "i18next";
import { toast } from "sonner";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import type { NavigationPayload } from "web_myplann/Utils/WebViewHandlers/Types/NotificationNavigationTypes";
import {
	type NavigationDestination,
	NavigationDestinationType,
	normalizeNavigationDestination,
} from "web_myplann/Utils/WebViewModel";

const NO_DEEP_LINK_GROUPS = new Set(["newevent", "portfolio", "silent", "old"]);

export const isNoDeepLinkGroup = (type: string | undefined): boolean =>
	!!type && NO_DEEP_LINK_GROUPS.has(type.trim().toLowerCase());

export type NavigateFn = (to: string) => Promise<void> | void;

type DestinationRoute = {
	path: (sourceId: string) => string;
	listRoute?: () => string;
};

const DESTINATION_ROUTES: Partial<
	Record<NavigationDestination, DestinationRoute>
> = {
	activity: {
		path: AppRouting.MyPlann.ActivityDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	activityTerm: {
		path: AppRouting.MyPlann.ActivityDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	activityTermNotice: {
		path: AppRouting.MyPlann.ActivityDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	task: {
		path: AppRouting.MyPlann.TaskDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	taskTerm: {
		path: AppRouting.MyPlann.TaskDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	taskTermNotice: {
		path: AppRouting.MyPlann.TaskDetail.path,
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	chat: {
		path: AppRouting.MyPlann.Chat.Detail.path,
	},
};

export const isReminderDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) === NavigationDestinationType.reminder ||
	notificationType?.toLowerCase() === "reminderterm";

export const isInvestmentQuestionnaireDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) ===
		NavigationDestinationType.investmentQuestionnaire ||
	notificationType?.toLowerCase() === "investmentquestionnaire";

export const isWebDestination = (type: string | undefined): boolean =>
	normalizeNavigationDestination(type) !== undefined;

export const getDestinationRoute = (type: string | undefined) => {
	const destination = normalizeNavigationDestination(type);
	return destination ? DESTINATION_ROUTES[destination] : undefined;
};

const logNavigationIssue = (reason: string, payload: NavigationPayload) =>
	logWarning(reason, {
		notification_type: payload.type,
		notification_id: payload.id,
		notification_source_id: payload.sourceId,
	});

export const fallbackToNotificationList = (
	reason: string,
	payload: NavigationPayload,
	navigate: NavigateFn,
) => {
	logNavigationIssue(reason, payload);
	toast.error(i18n.t(Resources.Common.somethingWentWrong));
	return navigate(AppRouting.MyPlann.Notifications.path());
};

export const navigateToSource = async (
	destination: DestinationRoute,
	payload: NavigationPayload,
	navigate: NavigateFn,
	getCurrentPath: () => string,
) => {
	if (!payload.sourceId) {
		await fallbackToNotificationList(
			"Notification navigation without sourceId",
			payload,
			navigate,
		);
		return;
	}

	if (destination.listRoute) {
		const listPath = destination.listRoute();
		if (getCurrentPath() !== listPath) {
			await navigate(listPath);
		}
	}

	await navigate(destination.path(payload.sourceId));
};
